/**
 * The feeds — the caller's own trail and a collection's history — and, since
 * OND-68, the one read on this screen that is NOT a feed.
 */
import type { AccessTrailResponse, ActivityResponse } from "@ondo/shared";

import { request } from "./request.ts";

/**
 * A collection's history — everything recorded about it and the works in it.
 *
 * Named for the subject rather than `fetchActivity`, because the endpoint takes
 * exactly one of `collectionId` or `workId` and the work variant will want the
 * other name.
 *
 * An empty array is a real answer twice over: nothing has been recorded, or the
 * feed store is not configured for this deployment. Neither is an error, and the
 * caller must not treat one as a failure.
 */
export const fetchMyActivity = () =>
  request<ActivityResponse>("/activity?scope=me").then((body) => body.activities);

/**
 * The caller's own trail — both sides of it.
 *
 * What lands here is what this account DID and what was DONE TO IT, because a
 * verification is fanned out to both parties' feeds. So one activity is read
 * from two sides, and which sentence to render is `activity.actor === my own
 * ONDO id` — a decision the client makes because only it knows who it is.
 *
 * No id in the query, and there cannot be one: the only feed this may open is
 * the caller's, and the server derives it from the session rather than from
 * anything sent.
 */
export const fetchCollectionActivity = (collectionId: string) =>
  request<ActivityResponse>(
    `/activity?collectionId=${encodeURIComponent(collectionId)}`,
  ).then((body) => body.activities);

/**
 * Who has opened what — the access trail.
 *
 * NOT A FEED, and that is the whole reason it is a separate read rather than
 * another `?scope=`. The two above come out of Stream, which is allowed to lose
 * a line because `ActivityFeedService.record()` swallows every error so an
 * outage cannot fail a write. This one comes out of `ondo.access_events`:
 * append-only, undeletable by anybody, and written inside the request that
 * served the thing it records. A screen may read a feed to answer "what
 * happened"; only this can answer "who looked".
 *
 * NO ID AND NO PARAMETERS, for the reason `fetchMyActivity` has none and one
 * more. `access_events_read` gives the caller their own rows plus every row of
 * a firm whose administration they hold, so the answer is already exactly the
 * caller's business — and an administrator of one firm reading another firm's
 * trail is a thing the policy makes impossible rather than a thing this
 * function declines to ask for.
 *
 * AN EMPTY ARRAY IS A REAL ANSWER AND MEANS TWO DIFFERENT THINGS depending on
 * the reader's rung — "the firm has opened nothing" for an administrator, "you
 * have opened nothing" for an ordinary seat. `accessEmptyFor` is where that is
 * decided; this function must not treat either as a failure.
 */
export const fetchAccessTrail = () =>
  request<AccessTrailResponse>("/access/trail").then((body) => body.events);
